import { useState, useEffect } from "react";
import { useAivyStudio } from "../../hooks/useAivyStudio";
import { toDataUri } from "../../utils/fileUtils";

import {
  Copy,
  Check,
  Play,
  Rocket,
  Lock,
  Plus,
  Trash2,
  GripVertical,
  FileText,
  Image,
  List,
  Type,
  ToggleLeft,
  HelpCircle,
  Sparkles,
  AlertCircle,
  ChevronRight,
  X,
  CheckCircle,
  Upload,
  User,
  Calendar,
  Phone,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../hooks/common/useTranslation";

const AivyStudio = () => {
  const navigate = useNavigate();
  const {
    generateAgent,
    testAgent,
    isGenerating,
    isTesting,
    error,
    clearError,
    handleImageUpload,
    uploadError,
  } = useAivyStudio();
  const { t, currentLanguage, changeLanguage } = useTranslation();

  const [isAuthorized, setIsAuthorized] = useState(true);
  const [surveyTitle, setSurveyTitle] = useState("");
  const [surveyPurpose, setSurveyPurpose] = useState("");
  const [collectPersonalInfo, setCollectPersonalInfo] = useState(false);
  const [requiredElements, setRequiredElements] = useState("");
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState("create");
  const [surveyImage, setSurveyImage] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hoveredQuestion, setHoveredQuestion] = useState(null);

  const [generatedAgent, setGeneratedAgent] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testResponses, setTestResponses] = useState({});
  const [showTestResult, setShowTestResult] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // 개인정보 필드 관리
  const [personalInfoFields, setPersonalInfoFields] = useState([
    { id: "name", label: "이름", icon: User, required: true },
    { id: "birthdate", label: "생년월일", icon: Calendar, required: false },
    { id: "phone", label: "휴대폰번호", icon: Phone, required: false },
    { id: "gender", label: "성별", icon: Users, required: false },
  ]);

  useEffect(() => {
    setPersonalInfoFields((prev) => [
      {
        id: "name",
        label: t("name"),
        icon: User,
        required: prev.find((f) => f.id === "name")?.required || true,
      },
      {
        id: "birthdate",
        label: t("birthdate"),
        icon: Calendar,
        required: prev.find((f) => f.id === "birthdate")?.required || false,
      },
      {
        id: "phone",
        label: t("phone"),
        icon: Phone,
        required: prev.find((f) => f.id === "phone")?.required || false,
      },
      {
        id: "gender",
        label: t("gender"),
        icon: Users,
        required: prev.find((f) => f.id === "gender")?.required || false,
      },
    ]);
  }, [currentLanguage, t]);
  const [customFields, setCustomFields] = useState([]);

  const deployUrl = "https://aivy.studio/agent/job_satisfaction_survey_2024";

  const handleLanguageChange = () => {
    const newLang = currentLanguage === "ko" ? "en" : "ko";
    changeLanguage(newLang);
  };

  // 개인정보 필드 추가
  const addCustomField = () => {
    const newField = {
      id: `custom_${Date.now()}`,
      label: t("addCustomField"),
      required: false,
      isCustom: true,
    };
    setCustomFields([...customFields, newField]);
  };

  // 개인정보 필드 삭제
  const removeCustomField = (fieldId) => {
    setCustomFields(customFields.filter((field) => field.id !== fieldId));
  };

  // 개인정보 필드 업데이트
  const updateCustomField = (fieldId, label) => {
    setCustomFields(
      customFields.map((field) =>
        field.id === fieldId ? { ...field, label } : field
      )
    );
  };

  // 개인정보 필드 필수 여부 토글
  const toggleFieldRequired = (fieldId, isCustom = false) => {
    if (isCustom) {
      setCustomFields(
        customFields.map((field) =>
          field.id === fieldId ? { ...field, required: !field.required } : field
        )
      );
    } else {
      setPersonalInfoFields(
        personalInfoFields.map((field) =>
          field.id === fieldId ? { ...field, required: !field.required } : field
        )
      );
    }
  };

  // 테스트용 임시 데이터
  const mockGeneratedAgent = {
    agent_id: "test_survey",
    agent_title: "직원 만족도 조사",
    agent_description:
      "회사 생활과 업무 환경에 대한 직원들의 의견을 수집하기 위한 설문조사입니다.",
    agent_schema: {
      properties: {
        satisfaction: {
          description: "전반적인 업무 만족도는 어떠신가요?",
          input_kind: "select",
          options: ["매우 만족", "만족", "보통", "불만족", "매우 불만족"],
        },
        worklife_balance: {
          description: "일과 삶의 균형이 잘 이루어지고 있나요?",
          input_kind: "text",
          placeholder: "자유롭게 의견을 작성해주세요.",
        },
        improvement: {
          description: "개선이 필요한 부분은 무엇인가요? (복수 선택 가능)",
          input_kind: "multiselect",
          options: [
            "업무 환경",
            "급여/복리후생",
            "업무 프로세스",
            "조직 문화",
            "성장 기회",
            "기타",
          ],
        },
        office_photo: {
          description: "현재 업무 공간의 사진을 공유해주세요.",
          input_kind: "image",
        },
      },
      required: ["satisfaction", "worklife_balance"],
    },
  };

  // 자동 생성된 에이전트 업데이트 함수
  const updateAgentSchema = (propertyKey, updates) => {
    if (!generatedAgent) return;

    const newAgent = { ...generatedAgent };
    const property = newAgent.agent_schema.properties[propertyKey];

    if (updates.description !== undefined)
      property.description = updates.description;
    if (updates.options !== undefined) property.options = updates.options;
    if (updates.placeholder !== undefined)
      property.placeholder = updates.placeholder;

    // required 배열 업데이트
    if (updates.required !== undefined) {
      if (
        updates.required &&
        !newAgent.agent_schema.required.includes(propertyKey)
      ) {
        newAgent.agent_schema.required.push(propertyKey);
      } else if (!updates.required) {
        newAgent.agent_schema.required = newAgent.agent_schema.required.filter(
          (key) => key !== propertyKey
        );
      }
    }

    setGeneratedAgent(newAgent);
  };

  // 문항 삭제
  const deleteQuestion = (keyToDelete) => {
    if (!generatedAgent) return;

    const newAgent = { ...generatedAgent };
    delete newAgent.agent_schema.properties[keyToDelete];

    // required 배열에서도 제거
    newAgent.agent_schema.required = newAgent.agent_schema.required.filter(
      (key) => key !== keyToDelete
    );

    setGeneratedAgent(newAgent);
    setSelectedQuestion(null);
  };

  const addOption = (propertyKey) => {
    if (!generatedAgent) return;
    const property = generatedAgent.agent_schema.properties[propertyKey];

    // multiselect의 경우 options 또는 items.enum 지원
    const currentOptions = property.options || property.items?.enum || [];
    const newOptions = [
      ...currentOptions,
      `새 옵션 ${currentOptions.length + 1}`,
    ];

    if (property.input_kind === "select") {
      updateAgentSchema(propertyKey, { options: newOptions });
    } else if (property.input_kind === "multiselect") {
      const newAgent = { ...generatedAgent };
      if (property.options) {
        // API 응답 형태: options 배열 사용
        newAgent.agent_schema.properties[propertyKey].options = newOptions;
      } else {
        // 기존 형태: items.enum 사용
        if (!newAgent.agent_schema.properties[propertyKey].items) {
          newAgent.agent_schema.properties[propertyKey].items = {};
        }
        newAgent.agent_schema.properties[propertyKey].items.enum = newOptions;
      }
      setGeneratedAgent(newAgent);
    }
  };

  const updateOption = (propertyKey, index, value) => {
    if (!generatedAgent) return;
    const property = generatedAgent.agent_schema.properties[propertyKey];

    if (property.input_kind === "select") {
      const newOptions = [...property.options];
      newOptions[index] = value;
      updateAgentSchema(propertyKey, { options: newOptions });
    } else if (property.input_kind === "multiselect") {
      const newAgent = { ...generatedAgent };

      if (property.options) {
        // API 응답 형태: options 배열 사용
        const newOptions = [...property.options];
        newOptions[index] = value;
        newAgent.agent_schema.properties[propertyKey].options = newOptions;
      } else {
        // 기존 형태: items.enum 사용
        const newOptions = [...(property.items?.enum || [])];
        newOptions[index] = value;
        if (!newAgent.agent_schema.properties[propertyKey].items) {
          newAgent.agent_schema.properties[propertyKey].items = {};
        }
        newAgent.agent_schema.properties[propertyKey].items.enum = newOptions;
      }
      setGeneratedAgent(newAgent);
    }
  };

  const removeOption = (propertyKey, index) => {
    if (!generatedAgent) return;
    const property = generatedAgent.agent_schema.properties[propertyKey];

    if (property.input_kind === "select") {
      const newOptions = property.options.filter((_, i) => i !== index);
      updateAgentSchema(propertyKey, { options: newOptions });
    } else if (property.input_kind === "multiselect") {
      const newAgent = { ...generatedAgent };

      if (property.options) {
        // API 응답 형태: options 배열 사용
        newAgent.agent_schema.properties[propertyKey].options =
          property.options.filter((_, i) => i !== index);
      } else {
        // 기존 형태: items.enum 사용
        if (property.items?.enum) {
          newAgent.agent_schema.properties[propertyKey].items.enum =
            property.items.enum.filter((_, i) => i !== index);
        }
      }
      setGeneratedAgent(newAgent);
    }
  };

  const handleGenerate = async () => {
    if (!surveyTitle && !surveyPurpose) {
      alert(t("pleaseEnterSurveyInfo"));
      return;
    }

    clearError();

    try {
      // API 호출 주석 처리
      /*const agent = await generateAgent({
        agent_id: surveyTitle || "새로운 설문조사",
        subject: surveyPurpose || "사용자 설문조사",
        using_feedback: requiredElements || "",
      });

      if (agent?.accessDenied) {
        setIsAuthorized(false);
        return;
      }*/

      // 임시 데이터 사용
      setGeneratedAgent(mockGeneratedAgent);
      setActiveTab("edit");
    } catch (error) {
      console.error("설문 생성 오류:", error);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(deployUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleTestSubmit = async () => {
    if (!generatedAgent) return;

    clearError();

    try {
      // 필수 필드 검증
      const requiredFields = generatedAgent.agent_schema.required || [];
      const missingFields = requiredFields.filter(
        (field) => !testResponses[field]
      );

      if (missingFields.length > 0) {
        alert(t("pleaseCompleteRequiredFields"));
        return;
      }

      // API 호출 주석 처리
      /*const result = await testAgent({
        spec: generatedAgent,
        input_values: testResponses,
      });*/

      // 임시 테스트 결과 데이터
      const mockTestResult = {
        generated_text:
          "설문 응답 분석 결과:\n\n1. 전반적인 만족도는 '만족' 수준으로 긍정적입니다.\n2. 일과 삶의 균형에 대해 구체적인 의견을 제시해 주셨습니다.\n3. 개선이 필요한 영역으로 '업무 프로세스'와 '성장 기회'를 선택하셨습니다.\n4. 업무 공간 사진을 통해 현재의 근무 환경을 확인할 수 있었습니다.\n\n제안된 개선사항들을 검토하여 더 나은 근무환경을 만들어가는 것이 좋겠습니다.",
        input_values: testResponses,
      };

      setTestResult(mockTestResult);
      setShowTestResult(true);
    } catch (error) {
      console.error("테스트 오류:", error);
    }
  };

  const resetTest = () => {
    setTestResponses({});
    setShowTestResult(false);
    setTestResult(null);
    setCurrentQuestionIndex(0);
    clearError();
  };

  const handleSurveyImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSurveyImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("auth") === "false") {
      setIsAuthorized(false);
    }
  }, []);

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl p-12 max-w-md w-full text-center border border-white/20">
          <div className="mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Lock className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-3">
              {t("accessRestricted")}
            </h2>
            <p className="text-gray-300">{t("expertAccountRequired")}</p>
          </div>
          <button className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl hover:shadow-lg transform hover:scale-105 transition-all duration-200 font-semibold">
            {t("login")}
          </button>
        </div>
      </div>
    );
  }

  const questionTypeIcons = {
    text: <Type className="w-4 h-4" />,
    select: <List className="w-4 h-4" />,
    multiselect: <ToggleLeft className="w-4 h-4" />,
    image: <Image className="w-4 h-4" />,
  };

  const questionTypeLabels = {
    text: t("questionTypes.text"),
    select: t("questionTypes.select"),
    multiselect: t("questionTypes.multiselect"),
    image: t("questionTypes.image"),
  };

  const questionTypeColors = {
    text: "bg-blue-100 text-blue-700",
    select: "bg-emerald-100 text-emerald-700",
    multiselect: "bg-purple-100 text-purple-700",
    image: "bg-amber-100 text-amber-700",
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 px-8 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-6">
          <button onClick={() => navigate("/")}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                {t("appName")}
              </h1>
            </div>
          </button>
          {generatedAgent && (
            <div className="flex items-center gap-3 px-4 py-2 bg-gray-50 rounded-xl">
              <FileText className="w-5 h-5 text-gray-500" />
              <span className="text-lg font-medium text-gray-700">
                {generatedAgent.agent_title}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleLanguageChange}
            className="px-6 py-3 rounded-xl flex items-center gap-3 font-semibold transition-all duration-200 border border-gray-200 shadow-md hover:shadow-lg transform hover:scale-105"
          >
            {currentLanguage === "ko" ? "한글" : "ENG"}
          </button>
          <button
            onClick={() => setShowDeployModal(true)}
            disabled={!generatedAgent}
            className={`px-6 py-3 rounded-xl flex items-center gap-3 font-semibold transition-all duration-200 shadow-md ${
              generatedAgent
                ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg transform hover:scale-105"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            <Rocket className="w-5 h-5" />
            {t("deploy")}
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-[25rem] bg-white/70 backdrop-blur-sm border-r border-gray-200/50 flex flex-col shadow-lg">
          <div className="p-6 border-b border-gray-200/50">
            <div className="flex gap-3">
              <button
                onClick={() => setActiveTab("create")}
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  activeTab === "create"
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {t("create")}
              </button>
              <button
                onClick={() => setActiveTab("edit")}
                className={`flex-1 py-3 px-4 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  activeTab === "edit"
                    ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                disabled={!generatedAgent}
              >
                {t("edit")}
              </button>
            </div>
          </div>

          {activeTab === "create" ? (
            <div className="p-6 space-y-6 overflow-y-auto">
              <div className="space-y-3">
                <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <span className="w-7 h-7 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-xs font-bold">
                    1
                  </span>
                  {t("surveyTitle")}
                </label>
                <input
                  type="text"
                  value={surveyTitle}
                  onChange={(e) => setSurveyTitle(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                  placeholder={t("surveyTitlePlaceholder")}
                />
              </div>

              <div className="space-y-3">
                <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <span className="w-7 h-7 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-xs font-bold">
                    2
                  </span>
                  {t("surveyPurpose")}
                </label>
                <textarea
                  value={surveyPurpose}
                  onChange={(e) => setSurveyPurpose(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200"
                  rows="4"
                  placeholder={t("surveyPurposePlaceholder")}
                />
              </div>

              <div className="space-y-3">
                <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <span className="w-7 h-7 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-xs font-bold">
                    3
                  </span>
                  {t("surveyImage")}
                </label>
                <div
                  onClick={() =>
                    document.getElementById("image-upload").click()
                  }
                  className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-purple-400 transition-all duration-200 cursor-pointer bg-white"
                >
                  {surveyImage ? (
                    <div className="relative">
                      <img
                        src={surveyImage}
                        alt="Survey"
                        className="max-h-32 mx-auto rounded-lg"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSurveyImage(null);
                        }}
                        className="absolute top-0 right-0 -mt-2 -mr-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">
                        {t("clickToUpload")}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {t("imageFormats")}
                      </p>
                    </>
                  )}
                </div>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleSurveyImageUpload}
                  className="hidden"
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={collectPersonalInfo}
                    onChange={(e) => setCollectPersonalInfo(e.target.checked)}
                    className="w-5 h-5 text-purple-500 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-700">
                      {t("collectPersonalInfo")}
                    </span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {t("collectPersonalInfoDesc")}
                    </p>
                  </div>
                </label>

                {collectPersonalInfo && (
                  <div className="ml-4 p-4 bg-purple-50 rounded-xl space-y-3">
                    {personalInfoFields.map((field) => (
                      <label
                        key={field.id}
                        className="flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2">
                          <field.icon className="w-4 h-4 text-purple-600" />
                          <span className="text-sm text-gray-700">
                            {field.label}
                          </span>
                        </div>
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={() => toggleFieldRequired(field.id)}
                          className="w-4 h-4 text-purple-500 rounded"
                        />
                      </label>
                    ))}

                    {customFields.map((field) => (
                      <div key={field.id} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={field.label}
                          onChange={(e) =>
                            updateCustomField(field.id, e.target.value)
                          }
                          className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm"
                          placeholder={t("fieldNamePlaceholder")}
                        />
                        <input
                          type="checkbox"
                          checked={field.required}
                          onChange={() => toggleFieldRequired(field.id, true)}
                          className="w-4 h-4 text-purple-500 rounded"
                        />
                        <button
                          onClick={() => removeCustomField(field.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}

                    <button
                      onClick={addCustomField}
                      className="w-full py-2 text-purple-600 text-sm font-medium hover:bg-purple-100 rounded-lg flex items-center justify-center gap-2 transition-all relative group"
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                    >
                      <Plus className="w-4 h-4" />
                      {t("addCustomField")}
                      {showTooltip && (
                        <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap">
                          {t("customFieldTooltip")}
                          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
                        </div>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <label className="text-sm font-bold text-gray-700 flex items-center gap-2">
                  <span className="w-7 h-7 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center text-xs font-bold">
                    4
                  </span>
                  {t("requiredElements")}
                </label>
                <textarea
                  value={requiredElements}
                  onChange={(e) => setRequiredElements(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200"
                  rows="3"
                  placeholder={t("requiredElementsPlaceholder")}
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-700">{error}</span>
                  <button
                    onClick={clearError}
                    className="ml-auto text-red-500 hover:text-red-700"
                  >
                    {t("close")}
                  </button>
                </div>
              )}

              <button
                onClick={handleGenerate}
                disabled={isGenerating || !surveyTitle}
                className={`w-full py-4 rounded-xl font-bold flex items-center justify-center gap-3 transition-all duration-200 ${
                  isGenerating || !surveyTitle
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg transform hover:scale-105"
                }`}
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    {t("aiGenerating")}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    {t("generateSurvey")}
                  </>
                )}
              </button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-3">
                  {generatedAgent &&
                    Object.entries(generatedAgent.agent_schema.properties).map(
                      ([key, property], index) => (
                        <div
                          key={key}
                          onClick={() =>
                            setSelectedQuestion({ key, property, index })
                          }
                          onMouseEnter={() => setHoveredQuestion(key)}
                          onMouseLeave={() => setHoveredQuestion(null)}
                          className={`p-3 rounded-xl border-2 transition-all duration-200 cursor-pointer transform group ${
                            selectedQuestion?.key === key
                              ? "border-purple-500 bg-purple-50 shadow-md scale-[1.02]"
                              : hoveredQuestion === key
                              ? "border-gray-300 bg-gray-50 shadow-sm scale-[1.01]"
                              : "border-gray-200 bg-white hover:border-gray-300"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-1">
                              <GripVertical className="w-5 h-5 text-gray-400" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span
                                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${
                                    questionTypeColors[property.input_kind]
                                  }`}
                                >
                                  {questionTypeIcons[property.input_kind]}
                                  {questionTypeLabels[property.input_kind]}
                                </span>
                                {generatedAgent.agent_schema.required.includes(
                                  key
                                ) && (
                                  <span className="px-2 py-1 bg-red-100 text-red-600 rounded-lg text-xs font-semibold">
                                    {t("required")}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm font-medium text-gray-800 line-clamp-2">
                                {index + 1}. {property.description}
                              </p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteQuestion(key);
                              }}
                              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all duration-200 p-1"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                            {selectedQuestion?.key === key && (
                              <ChevronRight className="w-5 h-5 text-purple-500 mt-1" />
                            )}
                          </div>
                        </div>
                      )
                    )}
                </div>
              </div>

              <div className="p-6 border-t border-gray-200/50">
                <button
                  onClick={() => {
                    resetTest();
                    setShowTestModal(true);
                  }}
                  className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-bold rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-3"
                >
                  <Play className="w-5 h-5" />
                  {t("testRun")}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 bg-gradient-to-br from-gray-50 to-gray-100 p-8 overflow-y-auto">
          {activeTab === "create" && !generatedAgent ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <Sparkles className="w-12 h-12 text-purple-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  {t("generateSurvey")}
                </h3>
                <p className="text-gray-600 whitespace-pre-line">
                  {t("enterSurveyInfo")}
                </p>
              </div>
            </div>
          ) : selectedQuestion ? (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">
                    {t("questionEdit")}
                  </h3>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700 block text-left">
                      {t("question")}
                    </label>
                    <input
                      type="text"
                      value={selectedQuestion.property.description}
                      onChange={(e) =>
                        updateAgentSchema(selectedQuestion.key, {
                          description: e.target.value,
                        })
                      }
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-lg transition-all duration-200"
                    />
                  </div>
                </div>

                {selectedQuestion.property.input_kind === "text" && (
                  <div className="mb-8">
                    <label className="text-sm font-semibold text-gray-700 mb-2 block text-left">
                      {t("placeholder")}
                    </label>
                    <input
                      type="text"
                      value={selectedQuestion.property.placeholder || ""}
                      onChange={(e) =>
                        updateAgentSchema(selectedQuestion.key, {
                          placeholder: e.target.value,
                        })
                      }
                      className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                      placeholder={t("placeholderExample")}
                    />
                  </div>
                )}

                {(selectedQuestion.property.input_kind === "select" ||
                  selectedQuestion.property.input_kind === "multiselect") && (
                  <div className="mb-8">
                    <label className="text-sm font-semibold text-gray-700 mb-4 block text-left">
                      {t("options")}
                    </label>
                    <div className="space-y-3">
                      {(() => {
                        // API 응답 형태(options) 또는 기존 형태(items.enum) 지원
                        const options =
                          selectedQuestion.property.options ||
                          selectedQuestion.property.items?.enum ||
                          [];
                        return options.map((option, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-3 group"
                          >
                            <span className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-sm font-semibold text-gray-600">
                              {index + 1}
                            </span>
                            <input
                              type="text"
                              value={option}
                              onChange={(e) =>
                                updateOption(
                                  selectedQuestion.key,
                                  index,
                                  e.target.value
                                )
                              }
                              className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                            />
                            <button
                              onClick={() =>
                                removeOption(selectedQuestion.key, index)
                              }
                              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all duration-200 p-2"
                              disabled={options.length <= 2}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        ));
                      })()}
                    </div>
                    <button
                      onClick={() => addOption(selectedQuestion.key)}
                      className="mt-4 px-5 py-3 text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-xl flex items-center gap-2 transition-all duration-200 font-semibold"
                    >
                      <Plus className="w-5 h-5" />
                      {t("addOption")}
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={generatedAgent.agent_schema.required.includes(
                          selectedQuestion.key
                        )}
                        onChange={(e) =>
                          updateAgentSchema(selectedQuestion.key, {
                            required: e.target.checked,
                          })
                        }
                        className="sr-only"
                      />
                      <div
                        className={`w-12 h-6 rounded-full transition-all duration-200 flex items-center px-1 ${
                          generatedAgent.agent_schema.required.includes(
                            selectedQuestion.key
                          )
                            ? "bg-purple-500"
                            : "bg-gray-300"
                        }`}
                      >
                        <div
                          className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-all duration-200 ${
                            generatedAgent.agent_schema.required.includes(
                              selectedQuestion.key
                            )
                              ? "translate-x-6"
                              : "translate-x-0"
                          }`}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      {t("required")}
                    </span>
                  </label>

                  <button
                    onClick={() => {
                      deleteQuestion(selectedQuestion.key);
                      setSelectedQuestion(null);
                    }}
                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2 transition-all duration-200"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t("deleteQuestion")}
                  </button>
                </div>
              </div>
            </div>
          ) : generatedAgent ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <HelpCircle className="w-12 h-12 text-gray-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">
                  {t("selectQuestionToEdit")}
                </h3>
                <p className="text-gray-600">{t("editQuestionInfo")}</p>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Deploy Modal */}
      {showDeployModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full transform transition-all duration-300 scale-100">
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {t("deployComplete")}
              </h3>
              <p className="text-gray-600">{t("deployMessage")}</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <p className="text-xs text-gray-500 mb-2">{t("deployUrl")}</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={deployUrl}
                  readOnly
                  className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm"
                />
                <button
                  onClick={handleCopy}
                  className={`px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 ${
                    copied
                      ? "bg-green-500 text-white"
                      : "bg-purple-500 text-white hover:bg-purple-600"
                  }`}
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  {copied ? t("copied") : t("copy")}
                </button>
              </div>
            </div>

            <button
              onClick={() => setShowDeployModal(false)}
              className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-200"
            >
              {t("close")}
            </button>
          </div>
        </div>
      )}

      {/* Test Modal */}
      {showTestModal && generatedAgent && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowTestModal(false);
              setCurrentQuestionIndex(0);
              resetTest();
            }
          }}
        >
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col h-auto max-h-[90vh]">
            {!showTestResult ? (
              <div className="flex flex-col h-full">
                <div className="p-8 pb-4">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    {generatedAgent.agent_title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {generatedAgent.agent_description}
                  </p>

                  <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${
                          ((currentQuestionIndex + 1) /
                            (Object.keys(generatedAgent.agent_schema.properties)
                              .length +
                              (collectPersonalInfo ? 1 : 0))) *
                          100
                        }%`,
                      }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-500 text-right">
                    {currentQuestionIndex + 1} /{" "}
                    {Object.keys(generatedAgent.agent_schema.properties)
                      .length + (collectPersonalInfo ? 1 : 0)}
                  </p>
                </div>

                {/* Questions Section */}
                <div className="flex-1 overflow-y-auto px-8 max-h-[20rem]">
                  {/* Personal Info Section */}
                  {collectPersonalInfo && currentQuestionIndex === 0 && (
                    <div className="mb-8 p-6 bg-gray-50 rounded-2xl">
                      <p className="text-sm font-semibold text-gray-700 mb-4">
                        개인정보
                      </p>
                      <div className="space-y-3">
                        {personalInfoFields.map(
                          (field) =>
                            field.required && (
                              <div
                                key={field.id}
                                className="flex items-center gap-3"
                              >
                                <field.icon className="w-5 h-5 text-gray-500" />
                                <input
                                  type={
                                    field.id === "birthdate" ? "date" : "text"
                                  }
                                  placeholder={field.label}
                                  className="flex-1 px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                                  value={testResponses[field.id] || ""}
                                  onChange={(e) =>
                                    setTestResponses({
                                      ...testResponses,
                                      [field.id]: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            )
                        )}
                        {customFields.map(
                          (field) =>
                            field.required && (
                              <input
                                key={field.id}
                                type="text"
                                placeholder={field.label}
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                                value={testResponses[field.id] || ""}
                                onChange={(e) =>
                                  setTestResponses({
                                    ...testResponses,
                                    [field.id]: e.target.value,
                                  })
                                }
                              />
                            )
                        )}
                      </div>
                    </div>
                  )}

                  {/* Questions */}
                  {Object.entries(generatedAgent.agent_schema.properties).map(
                    ([key, property], index) => {
                      const actualIndex = collectPersonalInfo
                        ? index + 1
                        : index;
                      if (actualIndex !== currentQuestionIndex) return null;

                      return (
                        <div key={key} className="mb-8 animate-fadeIn">
                          <p className="text-base font-medium text-gray-800 mb-3 text-left">
                            {index + 1}. {property.description}
                            {generatedAgent.agent_schema.required.includes(
                              key
                            ) && <span className="text-red-500 ml-1">*</span>}
                          </p>

                          {property.input_kind === "text" && (
                            <textarea
                              placeholder={property.placeholder}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none transition-all duration-200"
                              rows="3"
                              value={testResponses[key] || ""}
                              onChange={(e) =>
                                setTestResponses({
                                  ...testResponses,
                                  [key]: e.target.value,
                                })
                              }
                            />
                          )}

                          {property.input_kind === "select" && (
                            <select
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                              value={testResponses[key] || ""}
                              onChange={(e) =>
                                setTestResponses({
                                  ...testResponses,
                                  [key]: e.target.value,
                                })
                              }
                            >
                              <option value="">선택하세요</option>
                              {(property.options || []).map((option, i) => (
                                <option key={i} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          )}

                          {property.input_kind === "multiselect" && (
                            <div className="space-y-2">
                              {(
                                property.options ||
                                property.items?.enum ||
                                []
                              ).map((option, i) => (
                                <label
                                  key={i}
                                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all duration-200"
                                >
                                  <input
                                    type="checkbox"
                                    className="w-4 h-4 text-purple-600 rounded"
                                    checked={(
                                      testResponses[key] || []
                                    ).includes(option)}
                                    onChange={(e) => {
                                      const current = testResponses[key] || [];
                                      if (e.target.checked) {
                                        setTestResponses({
                                          ...testResponses,
                                          [key]: [...current, option],
                                        });
                                      } else {
                                        setTestResponses({
                                          ...testResponses,
                                          [key]: current.filter(
                                            (v) => v !== option
                                          ),
                                        });
                                      }
                                    }}
                                  />
                                  <span className="text-sm">{option}</span>
                                </label>
                              ))}
                            </div>
                          )}

                          {property.input_kind === "image" && (
                            <div
                              className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-purple-400 transition-all duration-200 cursor-pointer relative"
                              onClick={() =>
                                document.getElementById(`file-${key}`).click()
                              }
                            >
                              <input
                                id={`file-${key}`}
                                type="file"
                                accept=".png,.jpg,.jpeg"
                                className="hidden"
                                onChange={(e) => {
                                  handleImageUpload(
                                    key,
                                    e.target.files?.[0]
                                  ).then((imageData) => {
                                    if (imageData) {
                                      setTestResponses({
                                        ...testResponses,
                                        [key]: imageData,
                                      });
                                    }
                                  });
                                }}
                              />
                              {testResponses[key] ? (
                                <div className="relative group">
                                  <img
                                    src={toDataUri(testResponses[key])}
                                    alt="업로드된 이미지"
                                    className="w-full h-auto rounded-lg"
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center transition-all duration-300">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const newResponses = {
                                          ...testResponses,
                                        };
                                        delete newResponses[key];
                                        setTestResponses(newResponses);
                                      }}
                                      className="transform scale-0 group-hover:scale-100 transition-transform duration-300 bg-white bg-opacity-90 hover:bg-opacity-100 text-red-500 hover:text-red-600 p-2 rounded-full shadow-lg"
                                    >
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <Image className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                  <p className="text-sm text-gray-600">
                                    클릭하여 업로드
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    PNG, JPG, JPEG 형식
                                  </p>
                                </>
                              )}
                              {uploadError && (
                                <p className="text-sm text-red-500 mt-2">
                                  {uploadError}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                  )}
                </div>

                <div className="p-8 pt-4 border-t border-gray-200">
                  <div className="flex justify-between">
                    <button
                      onClick={() =>
                        setCurrentQuestionIndex(currentQuestionIndex - 1)
                      }
                      disabled={currentQuestionIndex === 0}
                      className={`px-6 py-3 border border-gray-200 rounded-xl transition-all duration-200 ${
                        currentQuestionIndex === 0
                          ? "opacity-50 cursor-not-allowed bg-gray-100"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      {t("previous")}
                    </button>

                    {currentQuestionIndex <
                    Object.keys(generatedAgent.agent_schema.properties).length -
                      1 +
                      (collectPersonalInfo ? 1 : 0) ? (
                      <button
                        onClick={() =>
                          setCurrentQuestionIndex(currentQuestionIndex + 1)
                        }
                        className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:bg-purple-600 transition-all duration-200"
                      >
                        {t("next")}
                      </button>
                    ) : (
                      <button
                        onClick={handleTestSubmit}
                        disabled={isTesting}
                        className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:bg-purple-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isTesting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            테스트 중...
                          </>
                        ) : (
                          "제출하기"
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                <div className="p-8 pb-4 border-b border-gray-200">
                  <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg animate-bounce">
                    <Check className="w-12 h-12 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-3">
                    테스트 완료!
                  </h3>
                  <p className="text-gray-600 text-lg mb-8">
                    설문 응답이 성공적으로 제출되었습니다.
                  </p>
                </div>
  
                <div className="flex-1 overflow-y-auto px-8 py-4 max-h-[30rem]">
                  {testResult && (
                    <>
                      <div className="bg-blue-50 rounded-2xl p-6 text-left mb-6">
                        <h4 className="font-semibold text-blue-700 mb-4">
                          AI 분석 결과
                        </h4>
                        <div className="text-sm text-blue-900 whitespace-pre-wrap leading-relaxed">
                          {testResult.generated_text}
                        </div>
                      </div>
  
                      <div className="bg-gray-50 rounded-2xl p-6 text-left">
                        <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                          <FileText className="w-5 h-5" />
                          제출된 응답
                        </h4>
                        <pre className="text-sm text-gray-600 whitespace-pre-wrap bg-white p-4 rounded-xl overflow-x-auto">
                          {JSON.stringify(
                            Object.fromEntries(
                              Object.entries(testResult.input_values).map(
                                ([key, value]) => [
                                  key,
                                  typeof value === "object" &&
                                  value !== null &&
                                  value.data
                                    ? {
                                        data: value.data.substring(0, 64) + "...",
                                        ext: value.ext,
                                      }
                                    : value,
                                ]
                              )
                            ),
                            null,
                            2
                          )}
                        </pre>
                      </div>
                    </>
                  )}
                </div>
  
                <div className="p-8 pt-4 border-t border-gray-200">
                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        setShowTestModal(false);
                        setCurrentQuestionIndex(0);
                        resetTest();
                      }}
                      className="flex-1 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-xl hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                    >
                      확인
                    </button>
                    <button
                      onClick={resetTest}
                      className="px-8 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-200"
                    >
                      다시 테스트
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AivyStudio;
